import { NavigateOptions } from 'react-router-dom'
import { AppsRoutes } from '../getRoute'
import { fillRoute } from '../fillRoute'
import { RouteProps } from '../types'
import { isNumber } from 'lodash/fp'
import { getParams, Params } from '../getParams'
import { getStore } from '@wellesley/store'
import { requestChangePath } from '@wellesley/host-remote/api/actions'
import {
  normalizeAtPathForBrowser,
  normalizeAtPathForRouter,
} from '../pathNormalizer'

type AppsNavigateOptions<T = RouteProps> = NavigateOptions & {
  queryParams?: Params
  props?: T
  target?: '_blank'
}

type WlsyRoute = AppsRoutes | number | ''

export const getFullRoute = <T extends RouteProps>(
  route: string | number,
  options?: AppsNavigateOptions<T>,
) => {
  const filledRoute =
    options?.props && !isNumber(route)
      ? fillRoute(route, options?.props)
      : route

  const queryString = getParams(options?.queryParams)

  return filledRoute + queryString
}

type ExternalNavigateOptions<T extends RouteProps> = AppsNavigateOptions<T> & {
  target: '_blank'
}
const isExternal = <T extends RouteProps>(
  options?: ExternalNavigateOptions<T> | AppsNavigateOptions<T>,
): options is ExternalNavigateOptions<T> => !!options?.target

function navigate<T extends RouteProps>(
  route: WlsyRoute,
  options?: AppsNavigateOptions<T>,
): void
function navigate<T extends RouteProps>(
  route: string,
  options: ExternalNavigateOptions<T>,
): void
function navigate<T extends RouteProps>(
  route: string | number,
  options?: AppsNavigateOptions<T>,
) {
  const fullRoute = normalizeAtPathForRouter(getFullRoute(route, options))

  if (isExternal<T>(options) && options.target === '_blank') {
    const externalRoute = normalizeAtPathForBrowser(fullRoute)
    const { origin } = new URL(document.referrer || window.location.origin)
    const hasDomain = externalRoute.includes('://')
    const url = hasDomain ? externalRoute : origin + externalRoute

    window.open(url, '_blank')
    return
  } else {
    getStore().dispatch(requestChangePath(fullRoute))
  }
}
export { navigate }
export const useAppsNavigate = <T extends RouteProps>() => {
  return navigate<T>
}
